import { useRef, useState } from "react";
import { BASE_URL } from "../data/constants";
import { getToken } from "../Services/authStore";
import { getUserLocation, UserLocation } from "../Services/LocationService";

export type VenueCandidate = { id: string; name: string; distanceM: number };

type GateState =
  | { status: "unknown" | "checking" }
  | { status: "blocked"; reason: string }
  | {
      status: "allowed";
      candidates: VenueCandidate[];
      selectedVenueId?: string;
      selectionToken?: string;
      pos?: UserLocation;
    };

type MeDTO = { id: string; role?: string };

export function useVenueGate() {
  const [gate, setGate] = useState<GateState>({ status: "unknown" });

  const ctxRef = useRef<{
    pos?: UserLocation;
    venueId?: string;
    selectionToken?: string;
    isSuperAdmin?: boolean;
  }>({});

  const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

  const ensureMe = async () => {
    // cache
    if (typeof ctxRef.current.isSuperAdmin === "boolean") return;

    try {
      const r = await fetch(`${BASE_URL}/me`, { headers: authHeader() });
      const me = (await r.json()) as MeDTO;
      ctxRef.current.isSuperAdmin = me?.role === "SUPERADMIN";
    } catch {
      // fail closed: treat as non-superadmin
      ctxRef.current.isSuperAdmin = false;
    }
  };

  const precheck = async () => {
    setGate({ status: "checking" });

    await ensureMe();
    const superadmin = !!ctxRef.current.isSuperAdmin;

    const pos = await getUserLocation();

    // SUPERADMIN: tillåt även sämre accuracy (du vill kunna testa hemma)
    if (!superadmin && pos.accuracy > 120) {
      setGate({ status: "blocked", reason: "location_inaccurate" });
      return { ok: false as const, reason: "location_inaccurate" };
    }

    const qs = new URLSearchParams();
    qs.set("lat", String(pos.lat));
    qs.set("lon", String(pos.lon));
    qs.set("accuracy", String(pos.accuracy));

    const r = await fetch(`${BASE_URL}/venues/nearby?${qs.toString()}`, {
      headers: authHeader(),
    });

    const data = await r.json();

    if (!r.ok || !data?.candidates?.length) {
      setGate({ status: "blocked", reason: "not_at_venue" });
      return { ok: false as const, reason: "not_at_venue" };
    }

    const prevVenueId = ctxRef.current.venueId;
    const stillValid =
      !!prevVenueId && data.candidates.some((c: VenueCandidate) => c.id === prevVenueId);

    const selectedVenueId =
      data.candidates.length === 1
        ? data.candidates[0].id
        : stillValid
        ? prevVenueId
        : undefined;

    setGate({
      status: "allowed",
      candidates: data.candidates,
      selectionToken: data.selectionToken,
      selectedVenueId,
      pos,
    });

    ctxRef.current = {
      ...ctxRef.current,
      pos,
      venueId: selectedVenueId,
      selectionToken: data.selectionToken,
    };

    return { ok: true as const, candidates: data.candidates, selectedVenueId };
  };

  const selectVenue = (venueId: string) => {
    setGate((g) => (g.status === "allowed" ? { ...g, selectedVenueId: venueId } : g));
    ctxRef.current.venueId = venueId;
  };

  // Denna behöver du när du postar updateVenueNowPlaying:
  // body måste inkludera selectionToken + lat/lon (för non-superadmin)
  const getCtx = () => ctxRef.current;

  return { gate, precheck, selectVenue, getCtx };
}
